import React, { useCallback, useMemo, useState } from "react";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    isArAgingReportPackageType,
    ReportPackageReportSetting,
} from "@common/types/reports/report-package";
import { AccountingReportType } from "@common/types/reports/report-type";
import { Button } from "@design-system/Button/Button";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { HStack } from "@design-system/HStack/HStack";
import {
    StandardModal,
    StandardModalHeader,
    StandardModalBody,
} from "@design-system/Modal/Modal";
import { Picker } from "@design-system/Picker/Picker.lib";
import { VStack } from "@design-system/VStack/VStack";
import { ReportPackageReportRow } from "./ReportPackageReportRow";
import { useReportPackageDownload } from "./useReportPackageDownload";
import { useReportPackageSettings } from "./useReportPackageSettings";
import styles from "./ReportPackageModal.module.scss";

interface ReportPackageModalProps {
    show: boolean;
    onHide: () => void;
    defaultEntityId?: number;
    defaultDateRange?: { start: string; end: string };
    ledgerId?: string;
}

export const ReportPackageModal: React.FC<ReportPackageModalProps> = ({
    show,
    onHide,
    defaultEntityId,
    defaultDateRange,
    ledgerId,
}) => {
    const {
        selectedEntityIds,
        setSelectedEntityIds,
        effectiveBasis,
        handleBasisChange: selectBasis,
        dateRange,
        setDateRange,
        pinnedPresets,
        onTogglePin,
        entityTriggerValue,
        entityItems,
        basisItems,
        availableReportTypes,
        resolvedLedgerId,
    } = useReportPackageSettings({
        show,
        defaultEntityId,
        defaultDateRange,
        ledgerId,
    });
    const [selectedReportTypes, setSelectedReportTypes] = useState<
        AccountingReportType[]
    >([]);
    const [reportSettings, setReportSettings] = useState<
        Partial<Record<AccountingReportType, ReportPackageReportSetting>>
    >({});

    const toggleReport = useCallback((type: AccountingReportType) => {
        setSelectedReportTypes((prev) =>
            prev.includes(type)
                ? prev.filter((t) => t !== type)
                : [...prev, type],
        );
    }, []);

    const handleBasisChange = useCallback(
        (nextBasis: AccountingLedgerBasis) => {
            selectBasis(nextBasis);
            if (nextBasis !== AccountingLedgerBasis.accruals) {
                setSelectedReportTypes((prev) =>
                    prev.filter((type) => !isArAgingReportPackageType(type)),
                );
            }
        },
        [selectBasis],
    );

    const updateReportSetting = useCallback(
        (
            type: AccountingReportType,
            update: Partial<ReportPackageReportSetting>,
        ) => {
            setReportSettings((prev) => ({
                ...prev,
                [type]: { ...prev[type], ...update },
            }));
        },
        [],
    );

    const reportTypesForDownload = useMemo(
        () =>
            selectedReportTypes.filter((type) =>
                availableReportTypes.some(
                    (availableType) => availableType === type,
                ),
            ),
        [selectedReportTypes, availableReportTypes],
    );

    const isDownloadEnabled =
        selectedEntityIds.length >= 1 &&
        reportTypesForDownload.length > 0 &&
        dateRange.start !== undefined &&
        dateRange.end !== undefined &&
        resolvedLedgerId !== undefined;

    const downloadParams = useMemo(() => {
        if (
            !isDownloadEnabled ||
            !dateRange.start ||
            !dateRange.end ||
            !resolvedLedgerId
        ) {
            return null;
        }

        const activeSettings: Record<string, ReportPackageReportSetting> = {};
        for (const type of reportTypesForDownload) {
            const setting = reportSettings[type];
            if (
                setting &&
                (setting.cycle ||
                    setting.comparisons?.length ||
                    setting.comparisonDiffs?.length ||
                    setting.ratioTypes?.length)
            ) {
                activeSettings[type] = setting;
            }
        }

        return {
            entityIds: selectedEntityIds,
            reportTypes: reportTypesForDownload,
            startDate: dateRange.start,
            endDate: dateRange.end,
            ledgerId: resolvedLedgerId,
            reportSettings:
                Object.keys(activeSettings).length > 0
                    ? activeSettings
                    : undefined,
        };
    }, [
        isDownloadEnabled,
        selectedEntityIds,
        reportTypesForDownload,
        dateRange,
        resolvedLedgerId,
        reportSettings,
    ]);

    const { download, isDownloading } =
        useReportPackageDownload(downloadParams);

    return (
        <StandardModal
            show={show}
            onHide={onHide}
            dialogClassName={styles.dialog}
        >
            <StandardModalHeader>Custom report package</StandardModalHeader>
            <StandardModalBody className={styles.body}>
                <VStack gap={16}>
                    <Field
                        label="Entities"
                        control="button"
                        className={styles.filterField}
                    >
                        <Picker.Select
                            multiple
                            items={entityItems}
                            value={selectedEntityIds}
                            onValueChange={setSelectedEntityIds}
                            placeholder="Select entities"
                            renderValue={() => entityTriggerValue}
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    size="md"
                                    className={styles.triggerButton}
                                />
                            }
                        />
                    </Field>
                    <Field
                        label="Basis"
                        control="button"
                        className={styles.filterField}
                    >
                        <Picker.Select
                            items={basisItems}
                            value={effectiveBasis}
                            onValueChange={(value) => {
                                if (value) {
                                    handleBasisChange(value);
                                }
                            }}
                            placeholder="Select basis"
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    size="md"
                                    className={styles.triggerButton}
                                />
                            }
                        />
                    </Field>
                    <Field
                        label="Date range"
                        control="button"
                        className={styles.filterField}
                    >
                        <Field.Control
                            render={(controlProps) => (
                                <DateRangeFilterNext
                                    onChange={setDateRange}
                                    dateRange={dateRange}
                                    requireSelection
                                    pinnedPresets={pinnedPresets}
                                    onTogglePin={onTogglePin}
                                    buttonClass={styles.triggerButton}
                                    fieldControlProps={pickFieldControlAriaProps(
                                        controlProps,
                                    )}
                                />
                            )}
                        />
                    </Field>
                </VStack>

                <div className={styles.filterField}>
                    <span className={styles.groupLabel}>Reports</span>
                    <div
                        className={styles.reportList}
                        role="group"
                        aria-label="Reports"
                    >
                        {availableReportTypes.map((type) => (
                            <ReportPackageReportRow
                                key={type}
                                type={type}
                                isSelected={selectedReportTypes.includes(type)}
                                setting={reportSettings[type]}
                                onToggle={toggleReport}
                                onUpdateSetting={updateReportSetting}
                            />
                        ))}
                    </div>
                </div>

                <HStack as="footer" gap={12} justify="end">
                    <Button variant="secondary" size="xl" onClick={onHide}>
                        Cancel
                    </Button>
                    <ButtonWithLoader
                        loading={isDownloading}
                        onClick={async () => {
                            const succeeded = await download();
                            if (succeeded) {
                                onHide();
                            }
                        }}
                        disabled={!isDownloadEnabled}
                        variant="primary"
                        size="xl"
                    >
                        Download
                    </ButtonWithLoader>
                </HStack>
            </StandardModalBody>
        </StandardModal>
    );
};
